import ApplicationController from './application'

export default class extends ApplicationController {

  static values = { noChangesMessage: String, belowTradedMessage: String }


  /*
   * IMPORTANT: This is the single gate that runs BEFORE the confirm -> OTP -> submit
   * sequence. EVERY allowance validation that should stop a submission must be enforced
   * HERE, not only server-side. Do NOT add new validations AFTER this sequence (i.e.
   * server-only), or the client suffers the full confirm + 2FA flow just to be rejected.
   * Server-side validations remain the source of truth, but must be mirrored here first.
   */
  handleKeyPress(event) {
    if (
      (event.target && event.target.classList.contains('save-changes')) ||
      (event.key && event.key.toLowerCase() === 'enter')
    ) {
      /* Prevent form submission when interacting with the open select list: */
      event.stopPropagation()
      event.preventDefault()

      const fields = this.element.querySelectorAll('[data-controller~="tradable-allowance"]')

      /* Skip the update entirely when nothing changed: */
      if (fields.length && !this.#hasChanges(fields)) return window.alert(this.noChangesMessageValue)

      /* Validate any active custom input min/max before confirming: */
      const invalid = this.#invalidCustom(fields)
      if (invalid) return invalid.reportValidity()

      /* Block reducing a limit below the amount already utilised this year, before confirming: */
      const traded = this.#belowTraded(fields)
      if (traded) return traded.reportValidity()

      /* Validate any required terms (unchecked checkbox) before continuing: */
      const unchecked = this.element.querySelector('input[type="checkbox"][data-terms-required]:not(:checked)')
      if (unchecked) {
        unchecked.required = true
        return unchecked.reportValidity()
      }

      this.#showConfirm(fields)
    }
  } // handleKeyPress


  confirmUpdate() {
    this.#confirmModal()?.classList.add('hidden')

    const otp = this.element.querySelector('.otp-modal')
    if (otp) {
      otp.classList.remove('hidden')
      otp.querySelector('input')?.focus()
    } else {
      this.element.requestSubmit()
    }
  } // confirmUpdate


  goBack() {
    this.#confirmModal()?.classList.add('hidden')
  } // goBack


  #showConfirm(fields) {
    const rows = this.element.querySelector('[data-confirm-rows]')
    if (rows) {
      rows.innerHTML = Array.from(fields).map((field) => {
        const hidden  = field.querySelector('[data-tradable-allowance-target="hidden"]')
        const select  = field.querySelector('[data-tradable-allowance-target="select"]')
        const current = field.dataset.tradableAllowanceCurrentValue || ''
        const next    = hidden && !hidden.disabled ? hidden.value : select ? select.value : ''
        const changed = String(next) !== String(current)
        const arrowCell = changed ? '<td class="py-3 px-2 text-center text-gray-500">&rarr;</td>' : '<td class="py-3 px-2"></td>'
        const newCell   = changed ? `<td class="py-3 pl-2 font-medium text-green-600">${this.#rand(next)}</td>` : '<td class="py-3 pl-2"></td>'
        return `<tr class="border-t"><td class="py-3 pr-4">${field.dataset.fieldLabel || ''}</td>` +
          `<td class="py-3 pr-2 text-gray-500">${this.#rand(current)}</td>` +
          arrowCell +
          newCell + '</tr>'
      }).join('')
    }

    this.#confirmModal()?.classList.remove('hidden')
  } // showConfirm


  #confirmModal() {
    return this.element.querySelector('.tradable-confirm-modal')
  } // confirmModal


  #rand(cents) {
    const n = Math.round(Number(cents) / 100)
    return Number.isFinite(n) ? `R${n.toLocaleString('en-US')}` : '-'
  } // rand


  #invalidCustom(fields) {
    return Array.from(fields).map((field) => {
      const wrap   = field.querySelector('[data-tradable-allowance-target="customWrap"]')
      const custom = field.querySelector('[data-tradable-allowance-target="custom"]')
      const active = wrap && wrap.style.display !== 'none'
      const v      = custom && custom.validity
      /* Only enforce min/max, our 0-or-min rule (customError) and bad/empty input in custom mode, never step alignment: */
      return active && v && (v.rangeUnderflow || v.rangeOverflow || v.customError || v.badInput || v.valueMissing) ? custom : null
    }).find(Boolean)
  } // invalidCustom


  #belowTraded(fields) {
    return Array.from(fields).map((field) => {
      const used    = Number(field.dataset.used)
      const hidden  = field.querySelector('[data-tradable-allowance-target="hidden"]')
      const select  = field.querySelector('[data-tradable-allowance-target="select"]')
      const control = hidden && !hidden.disabled ? field.querySelector('[data-tradable-allowance-target="custom"]') : select
      const raw     = hidden && !hidden.disabled ? hidden.value : select ? select.value : ''
      const value   = Number(raw)
      control?.setCustomValidity('')
      /* Block any value below the amount already utilised — including opting out (0) once traded.
         Empty (raw === '') is left to presence handling, not flagged as "below traded": */
      if (!used || raw === '' || value >= used) return null
      control.setCustomValidity(this.belowTradedMessageValue.replace('%{amount}', this.#rand(used)))
      return control
    }).find(Boolean)
  } // belowTraded


  #hasChanges(fields) {
    return Array.from(fields).some((field) => {
      const current = field.dataset.tradableAllowanceCurrentValue || ''
      const hidden  = field.querySelector('[data-tradable-allowance-target="hidden"]')
      const select  = field.querySelector('[data-tradable-allowance-target="select"]')
      const value   = hidden && !hidden.disabled ? hidden.value : select ? select.value : ''
      return String(value) !== String(current)
    })
  } // hasChanges

}
